import { BASE_URL } from '../../../shared/lib/api';

export interface HideItemRequest {
  account_id: string;
  ticker?: string | null;
  event_type?: string | null;
  transaction_id?: string | null;
}

export interface UnhideItemRequest {
  account_id: string;
  ticker?: string | null;
  event_type?: string | null;
  transaction_id?: string | null;
}

export interface HiddenItem {
  id: string;
  user_id: string;
  account_id: string;
  ticker: string | null;
  event_type: string | null;
  transaction_id: string | null;
  is_hidden: boolean;
  created_at: string;
  updated_at: string;
}

export interface HideItemResponse {
  success: boolean;
  error?: string;
}

export interface UnhideItemResponse {
  success: boolean;
  error?: string;
}

export interface GetHiddenItemsResponse {
  hidden_items: HiddenItem[];
}

/**
 * Hide an item (account, ticker, interest, or cash transaction).
 * 
 * @param token - Authentication token
 * @param request - Hide item request parameters
 * @returns Promise with success status
 */
export async function hideItem(
  token: string,
  request: HideItemRequest
): Promise<HideItemResponse> {
  const url = `${BASE_URL}/hidden-items/hide`;
  
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  
  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: `hide-item failed: ${res.status}` }));
    throw new Error(error.error || `hide-item failed: ${res.status}`);
  }
  const json = (await res.json()) as HideItemResponse;
  return json;
}

/**
 * Unhide an item (account, ticker, interest, or cash transaction).
 * 
 * @param token - Authentication token
 * @param request - Unhide item request parameters
 * @returns Promise with success status
 */
export async function unhideItem(
  token: string,
  request: UnhideItemRequest
): Promise<UnhideItemResponse> {
  const url = `${BASE_URL}/hidden-items/unhide`;
  
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify(request),
  });
  
  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: `unhide-item failed: ${res.status}` }));
    throw new Error(error.error || `unhide-item failed: ${res.status}`);
  }
  const json = (await res.json()) as UnhideItemResponse;
  return json;
}

/**
 * Get all hidden items for an account.
 * 
 * @param token - Authentication token
 * @param accountId - Account ID
 * @returns Promise with list of hidden items
 */
export async function getHiddenItems(
  token: string,
  accountId: string
): Promise<GetHiddenItemsResponse> {
  const res = await fetch(`${BASE_URL}/hidden-items/${accountId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (res.status === 401) throw new Error('unauthorized');
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: `get-hidden-items failed: ${res.status}` }));
    throw new Error(error.error || `get-hidden-items failed: ${res.status}`);
  }
  const json = (await res.json()) as GetHiddenItemsResponse;
  return json;
}

